import { type ChangeEvent, memo, useMemo } from 'react';
import {
    Box,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { getMinPriceFromDualCurrency } from '@/utils/getMinPriceFromDualCurrency';
import StepContent from '@/views/add-product/components/step-content';
import type { PackageFormValues } from '../../add-package.types';
import { calcDiscount, getUniqueProducts } from './heleprs';

const PricingStep = ({ errors }: { errors: string[] }) => {
    const { control, setValue } = useFormContext<PackageFormValues>();

    const products = useWatch({
        control,
        name: 'products',
    });
    const uniqueProducts = useMemo(
        () => getUniqueProducts(products),
        [products]
    );

    const discount = useWatch({
        control,
        name: 'discount',
    });
    const globalCurrency = useWatch({
        control,
        name: 'globalCurrency',
    });
    const baseTotal = uniqueProducts.reduce(
        (acc, product) => acc + getMinPriceFromDualCurrency(product.price),
        0
    );
    const discountAmount = calcDiscount(discount, baseTotal);
    const finalTotal = Math.max(0, baseTotal - discountAmount);

    const handleDiscount = (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        const digitsOnly = raw.replace(/\D/g, '');
        const nextValue = digitsOnly === '' ? 0 : Number(digitsOnly);

        if (discount.type === 'relative') {
            const clamp = Math.min(100, Math.max(0, nextValue));
            setValue('discount.amount', clamp);
            return;
        }

        setValue('discount.amount', nextValue);
    };

    const handleNext = () => {
        setValue('total', finalTotal, { shouldDirty: true });
    };

    return (
        <StepContent
            title="Pricing and Financial"
            errors={errors}
            onClick={handleNext}
        >
            <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={3}>
                    <Typography variant="h6" fontWeight={600}>
                        Products
                    </Typography>
                    {uniqueProducts.map(item => (
                        <Stack
                            key={item.id}
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            sx={{ borderBottom: '1px solid #E0E0E0' }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight={600}>
                                    {item.name}
                                </Typography>
                            </Box>
                            <Typography variant="body1">
                                {getMinPriceFromDualCurrency(item.price)}
                                <small>{globalCurrency}</small>
                            </Typography>
                        </Stack>
                    ))}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            borderBottom: '1px solid #E0E0E0',
                        }}
                    >
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{
                                color: 'text.secondary',
                                marginTop: 1,
                            }}
                        >
                            Subtotal
                        </Typography>
                        <Typography variant="h6" fontWeight={800}>
                            {baseTotal}
                            <small>{globalCurrency}</small>
                        </Typography>
                    </Stack>

                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={2}
                        width="100%"
                    >
                        <TextField
                            fullWidth
                            inputProps={{
                                min:
                                    discount.type === 'relative'
                                        ? 0
                                        : undefined,
                                max:
                                    discount.type === 'relative'
                                        ? 100
                                        : undefined,
                                inputMode: 'numeric',
                            }}
                            onChange={handleDiscount}
                            value={discount?.amount ?? 0}
                        />
                        <Controller
                            control={control}
                            name="discount.type"
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    value={field.value}
                                    sx={{
                                        minWidth: 124,
                                        '& .MuiSelect-select': {
                                            fontWeight: 700,
                                        },
                                    }}
                                >
                                    <MenuItem value="relative">%</MenuItem>
                                    <MenuItem value="absolute">
                                        {globalCurrency || 'Absolute'}
                                    </MenuItem>
                                </Select>
                            )}
                        />
                    </Stack>
                    {discount?.amount > 0 && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{ color: 'text.secondary' }}
                            >
                                Discount
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight={800}
                                sx={{ color: 'error.main' }}
                            >
                                {discountAmount
                                    ? `-${discountAmount}`
                                    : discountAmount}
                                <small>{globalCurrency}</small>
                            </Typography>
                        </Stack>
                    )}

                    <Paper
                        variant="elevation"
                        sx={{
                            borderRadius: 0.5,
                            px: 3,
                            py: 2.5,
                            bgcolor: theme => theme.palette.action.hover,
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography variant="h6" fontWeight={800}>
                                Total
                            </Typography>

                            <Typography variant="h4" fontWeight={900}>
                                {finalTotal}
                                <small>{globalCurrency}</small>
                            </Typography>
                        </Stack>
                    </Paper>
                </Stack>
            </Paper>
        </StepContent>
    );
};

export default memo(PricingStep);
